<template>
  <div style="padding: 16px; background: #F8F8F9;">
    <div class="cm-panel">
      <div class="cm-panel-head">
        <div class="cm-panel-title">Assignment Rules<span class="sub" v-if="ruleCount">{{ ruleCount }} programs</span></div>
        <div class="cm-panel-head-actions">
          <button class="cm-btn cm-btn-secondary cm-btn-sm" @click="runRebalance" :disabled="rebalancing || balancing">
            <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" style="font-size: 11px;" />
            {{ rebalancing ? 'Assigning…' : 'Assign Unassigned' }}
          </button>
          <button class="cm-btn cm-btn-secondary cm-btn-sm" @click="previewBalance" :disabled="rebalancing || balancing">
            <FontAwesomeIcon :icon="['fas', 'scale-balanced']" style="font-size: 11px;" />
            {{ balancing ? 'Balancing…' : 'Rebalance Opportunities' }}
          </button>
        </div>
      </div>
      <Worklist_Assignments
        v-if="metricsLoaded"
        :metric-map="metricMap"
        :member-entity-types="['CM Manager', 'CM Staff']"
        member-label="Care Manager"
        stage-label="Stage"
        :task-type-map="cmTaskTypeMap"
        height="auto"
      />
    </div>

    <!-- Rebalance Result Modal -->
    <Teleport to="body">
      <div v-if="rebalanceResult" class="cm-overlay" @click.self="rebalanceResult = null">
        <div class="cm-modal">
          <div class="cm-modal-head">
            <h3>Assignment Result</h3>
            <button class="cm-icon-btn" @click="rebalanceResult = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <template v-if="rebalanceResult.error">
              <div style="font-size: 12.5px; color: #D7402B;">{{ rebalanceResult.error }}</div>
            </template>
            <template v-else>
              <div style="font-size: 26px; font-weight: 700; color: #353537;">{{ rebalanceResult.tasks_moved }} tasks assigned</div>
              <div v-if="rebalanceResult.tasks_no_candidates > 0" style="font-size: 12.5px; color: #B45309;">
                {{ rebalanceResult.tasks_no_candidates }} tasks could not be assigned (no matching rules)
              </div>
              <div v-if="rebalanceResult.unassignable_reasons && rebalanceResult.unassignable_reasons.length" style="display: flex; flex-direction: column; gap: 6px; margin-top: 8px;">
                <div v-for="(r, i) in rebalanceResult.unassignable_reasons" :key="i"
                  style="padding: 6px 8px; border-radius: 6px; font-size: 11.5px; background: #FDF4E7;">
                  <span style="font-weight: 500; color: #353537;">{{ r.program }}</span>
                  <span style="color: #A5A5A6;"> · </span>
                  <span style="color: #626264;">{{ r.task_type }}</span>
                  <span style="color: #A5A5A6;"> · </span>
                  <span style="font-weight: 600; color: #92400E;">{{ r.count }} tasks</span>
                  <div style="font-size: 10px; margin-top: 2px; color: #92400E;">{{ r.reason }}</div>
                </div>
              </div>
              <div v-if="rebalanceResult.tasks_moved === 0 && !rebalanceResult.tasks_no_candidates" style="font-size: 12.5px; color: #A5A5A6;">
                No unassigned tasks found that match existing rules.
              </div>
            </template>
          </div>
          <div class="cm-modal-foot">
            <button class="cm-btn cm-btn-primary" @click="rebalanceResult = null">Done</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Rebalance Preview / Confirm Modal -->
    <Teleport to="body">
      <div v-if="balancePreview" class="cm-overlay" @click.self="closeBalance">
        <div class="cm-modal" style="max-width: 560px;">
          <div class="cm-modal-head">
            <h3>{{ balanceApplied ? 'Rebalance Complete' : 'Rebalance Preview' }}</h3>
            <button class="cm-icon-btn" @click="closeBalance"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <template v-if="balancePreview.error">
              <div style="font-size: 12.5px; color: #D7402B;">{{ balancePreview.error }}</div>
            </template>
            <template v-else>
              <div style="font-size: 26px; font-weight: 700; color: #353537;">
                {{ balancePreview.tasks_moved }} {{ balanceApplied ? 'opportunities moved' : 'opportunities would move' }}
              </div>
              <div style="font-size: 12px; color: #626264; margin-top: 2px;">
                Only opportunities whose assignment rules allow more than one care manager can move —
                single-owner practices and contract-scoped cases stay put.
              </div>
              <div v-if="balancePreview.tasks_no_candidates > 0" style="font-size: 12px; color: #B45309; margin-top: 6px;">
                {{ balancePreview.tasks_no_candidates }} opportunities can’t be rebalanced (single owner or no matching rule).
              </div>

              <!-- Before / After caseloads -->
              <div v-if="balanceRows.length" style="margin-top: 14px;">
                <div style="display: grid; grid-template-columns: 1fr 56px 56px; gap: 4px 10px; font-size: 11px; color: #A5A5A6; text-transform: uppercase; letter-spacing: .04em; padding-bottom: 4px; border-bottom: 1px solid #EEE;">
                  <div>Care Manager</div><div style="text-align:right;">Before</div><div style="text-align:right;">After</div>
                </div>
                <div style="max-height: 300px; overflow-y: auto;">
                  <div v-for="r in balanceRows" :key="r.name"
                    style="display: grid; grid-template-columns: 1fr 56px 56px; gap: 4px 10px; font-size: 12.5px; padding: 4px 0; align-items: center;">
                    <div style="color: #353537;">{{ r.name }}</div>
                    <div style="text-align:right; color: #A5A5A6;">{{ r.before }}</div>
                    <div style="text-align:right; font-weight: 600;"
                      :style="{ color: r.after < r.before ? '#059669' : (r.after > r.before ? '#B45309' : '#626264') }">
                      {{ r.after }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else style="font-size: 12.5px; color: #A5A5A6; margin-top: 10px;">
                Nothing to rebalance — every eligible opportunity is already at the balanced owner.
              </div>
            </template>
          </div>
          <div class="cm-modal-foot" style="display: flex; gap: 8px; justify-content: flex-end;">
            <template v-if="balanceApplied || balancePreview.error">
              <button class="cm-btn cm-btn-primary" @click="closeBalance">Done</button>
            </template>
            <template v-else>
              <button class="cm-btn cm-btn-secondary" @click="closeBalance">Cancel</button>
              <button class="cm-btn cm-btn-primary" :disabled="balancing || balancePreview.tasks_moved === 0" @click="applyBalance">
                {{ balancing ? 'Applying…' : 'Apply Rebalance' }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Worklist_Assignments from '@/components/widgetcomponents/Worklist_Assignments.vue';

export default {
  name: 'TeamAssignments',
  components: { FontAwesomeIcon, Worklist_Assignments },
  data() {
    return {
      metricMap: {},          // { 'Program Name': metricId } for the current tenant
      metricIdToName: {},     // { metricId: 'Program Name' }
      metricsLoaded: false,
      cmTaskTypeMap: [],
      rebalancing: false,
      rebalanceResult: null,
      ruleCount: null,
      // Rebalance Opportunities (within-rule caseload equalization)
      balancing: false,
      balancePreview: null,   // dry-run result shown for confirmation
      balanceApplied: false,  // flips true after Apply succeeds
    };
  },
  computed: {
    balanceRows() {
      const p = this.balancePreview;
      if (!p || p.error) return [];
      const before = p.caseloads_before || {};
      const after = p.caseloads_after || {};
      const names = new Set([...Object.keys(before), ...Object.keys(after)]);
      return [...names]
        .map(name => ({ name, before: before[name] || 0, after: after[name] || 0 }))
        .sort((a, b) => b.after - a.after || a.name.localeCompare(b.name));
    },
  },
  methods: {
    async fetchMetrics() {
      // Tenant-scoped: pull this tenant's Care Management metrics. No hardcoded IDs.
      try {
        const { data } = await axios.get('/api/metrics/', {
          params: { metric_category: 'Care Management' }
        });
        const metrics = data.results || data;
        const map = {};
        const idToName = {};
        for (const m of metrics) {
          const name = m.display_name || m.name;
          map[name] = m.id;
          idToName[m.id] = name;
        }
        this.metricMap = map;
        this.metricIdToName = idToName;
      } catch (e) {
        console.error('Error fetching care management metrics:', e);
      } finally {
        this.metricsLoaded = true;
      }
    },
    async fetchTaskTypeMap() {
      const metricIds = Object.values(this.metricMap).join(',');
      if (!metricIds) { this.cmTaskTypeMap = []; this.ruleCount = 0; return; }
      try {
        const { data } = await axios.get('/api/metrictasktypes/', { params: { metric_ids: metricIds } });
        this.cmTaskTypeMap = data.map(mtt => ({
          program: this.metricIdToName[mtt.metric] || `Metric ${mtt.metric}`,
          tier: mtt.task_type_details?.name || `Task Type ${mtt.task_type}`,
          id: mtt.task_type,
        }));
        // Count unique programs
        this.ruleCount = new Set(this.cmTaskTypeMap.map(t => t.program)).size;
      } catch (e) { console.error('Error fetching task type map:', e); }
    },
    async runRebalance() {
      this.rebalancing = true;
      try {
        const { data } = await axios.post('/api/team-assignments/rebalance/', {
          dry_run: false, unassigned_only: true,
          metric_ids: Object.values(this.metricMap),
        });
        this.rebalanceResult = data;
      } catch (e) {
        this.rebalanceResult = { error: e.response?.data?.error || e.message || 'Assignment failed' };
      } finally { this.rebalancing = false; }
    },
    // Rebalance Opportunities: preview (dry-run) -> confirm -> apply. This MOVES
    // existing assignments (not just fills blanks), so it's gated behind a
    // before/after preview. unassigned_only omitted -> full within-rule balance.
    async previewBalance() {
      this.balancing = true;
      this.balanceApplied = false;
      try {
        const { data } = await axios.post('/api/team-assignments/rebalance/', {
          dry_run: true,
          metric_ids: Object.values(this.metricMap),
        });
        this.balancePreview = data;
      } catch (e) {
        this.balancePreview = { error: e.response?.data?.error || e.message || 'Preview failed' };
      } finally { this.balancing = false; }
    },
    async applyBalance() {
      this.balancing = true;
      try {
        const { data } = await axios.post('/api/team-assignments/rebalance/', {
          dry_run: false,
          metric_ids: Object.values(this.metricMap),
        });
        this.balancePreview = data;   // fresh actual result (before/after)
        this.balanceApplied = true;
      } catch (e) {
        this.balancePreview = { error: e.response?.data?.error || e.message || 'Rebalance failed' };
      } finally { this.balancing = false; }
    },
    closeBalance() {
      this.balancePreview = null;
      this.balanceApplied = false;
    },
  },
  async mounted() {
    await this.fetchMetrics();
    await this.fetchTaskTypeMap();
  },
};
</script>
